import React, { useEffect, useState, useRef } from 'react'
import map from 'lodash/map'
import find from 'lodash/find'
import ScrollingLoader from '@SiteComponents/ScrollingLoader'
import ProjectCard from './ProjectCard'
import Plus from '@Images/projects_table_new_project_plus.svg'

import {
  IProject,
  IProjectLabelCaptions,
} from '@SiteModules/Project/types'

import {
  IProjectVersion,
} from '@SiteModules/ProjectVersion/types'

import {
  IOpenRenameProjectPopupPayload,
  IOpenDeleteProjectPopupPayload,
  IOpenSetupProjectPopupPayload,
  IOpenSetupProjectPopupTtsPayload,
} from '@SiteModules/Popup/types'

import './ProjectsTableGrid.scss'

const MIN_CARD_WIDTH = 259.5 // px
const GAP_X = 16 // px, as in ProjectsTableGrid.scss
const ASPECT_RATIO_THUMBNAIL = 235.5 / 176 // From Figma design
const THUMBNAIL_PADDING = 12 // px, on each side
const PROJECT_CARD_BOTTOM_HEIGHT = 68 // px

const SCALING_FACTOR_FOR_NEW_PROJECT_CARD_WHEN_HOVERING = 1.04 // as in ProjectsTableGrid.scss

interface ProjectsTableGridProps {
  readonly className: string
  readonly projects: IProject[]
  readonly selectedProjectIds: string[]
  readonly loading: boolean
  readonly listActive: boolean
  readonly projectLabelCaptions: IProjectLabelCaptions[]
  readonly hasLoadedAllProjects: boolean
  readonly hasValidCurrentWorkspace: boolean
  readonly currentAccountHasCurrentSubscription: boolean
  openNewProjectPopup(): void
  openRenewSubscriptionPopup(): void
  changeSelectedProjects(e: React.ChangeEvent<HTMLInputElement>, projectId: string): void
  handleForwarding(project: IProject): void
  handleOpenRenameProjectPopup(payload: IOpenRenameProjectPopupPayload): void
  handleOpenDeleteProjectPopup(payload: IOpenDeleteProjectPopupPayload): void
  handleOpenSetupProjectPopup(payload: IOpenSetupProjectPopupPayload): void
  handleOpenSetupProjectPopupTts(payload: IOpenSetupProjectPopupTtsPayload): void
  handleOpenRenewSubscriptionPopup(): void
  handleOpenExportationPopup(projectId: string): void
}

const ProjectsTableGrid: React.FC<ProjectsTableGridProps> = props => {
  const {
    projects,
    selectedProjectIds,
    className,
    loading,
    listActive,
    projectLabelCaptions,
    hasLoadedAllProjects,
    hasValidCurrentWorkspace,
    currentAccountHasCurrentSubscription,
  } = props

  const gridRef = useRef<HTMLDivElement>(null)
  const [cardWidth, setCardWidth] = useState(MIN_CARD_WIDTH)
  const [hasScrolled, setHasScrolled] = useState(false)
  const thumbnailHeight = (cardWidth - 2 * THUMBNAIL_PADDING) / ASPECT_RATIO_THUMBNAIL + 2 * THUMBNAIL_PADDING
  const cardHeight = thumbnailHeight + PROJECT_CARD_BOTTOM_HEIGHT
  const showFooter = !loading && hasLoadedAllProjects && hasScrolled

  useEffect(() => {
    calculateCardWidth()
    window.addEventListener('resize', calculateCardWidth)
    return () => window.removeEventListener('resize', calculateCardWidth)
  }, [])

  useEffect(() => {
    const container = gridRef.current

    if (!container) { return }

    const handleScroll = () => setHasScrolled(container.scrollTop > 0)

    container.addEventListener('scroll', handleScroll)

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [gridRef.current])

  useEffect(() => {
    if (!listActive) {
      calculateCardWidth()
    }
  }, [listActive])

  const calculateCardWidth = () => {
    const container = gridRef.current

    if (!container) { return }

    const containerWidth = container.offsetWidth

    // max number of cards that can fit while respecting min width + gaps
    let maxCards = Math.floor((containerWidth + GAP_X) / (MIN_CARD_WIDTH + GAP_X))

    if (maxCards < 1) {
      maxCards = 1
    }

    // recalculate card width so that all cards + gaps exactly fit container width
    const totalGap = GAP_X * (maxCards - 1)
    const dynamicCardWidth = (containerWidth - totalGap) / maxCards

    setCardWidth(dynamicCardWidth)
  }

  const renderNewProject = () => {
    const newProjectWidth = cardWidth / SCALING_FACTOR_FOR_NEW_PROJECT_CARD_WHEN_HOVERING
    const newProjectHeight = cardHeight / SCALING_FACTOR_FOR_NEW_PROJECT_CARD_WHEN_HOVERING
    const newProjectMarginHorizontal = (cardWidth - newProjectWidth) / 2
    const newProjectMarginVertical = (cardHeight - newProjectHeight) / 2
    const newProjectStyle = {
      width: `${newProjectWidth}px`,
      height: `${newProjectHeight}px`,
      margin: `${newProjectMarginVertical}px ${newProjectMarginHorizontal}px`
    }

    return (
      <div
        className="ProjectsTableGrid__card ProjectsTableGrid__new-project"
        style={newProjectStyle}
        onClick={currentAccountHasCurrentSubscription ? props.openNewProjectPopup : props.openRenewSubscriptionPopup}
      >
        <div className="ProjectsTableGrid__new-project-center">
          <div className="ProjectsTableGrid__new-project-center-icon">
            <Plus />
          </div>
          <div className="ProjectsTableGrid__new-project-center-text">
            New project
          </div>
        </div>
      </div>
    )
  }

  const projectsList = map(projects, project => {
    const selected = selectedProjectIds.includes(project.id)
    const labelCaptions = find(projectLabelCaptions, projectLabelCaption => projectLabelCaption.projectId === project.id)
    const labelCaptionsOfProject = labelCaptions ? labelCaptions.captions : []
    const originalProjectVersion = find(project.projectVersions, projectVersion => !projectVersion.attributes.isRepurposed) as IProjectVersion

    return (
      <ProjectCard
        key={project.id}
        project={project}
        originalProjectVersion={originalProjectVersion}
        selected={selected}
        changeSelectedProjects={props.changeSelectedProjects}
        handleForwarding={props.handleForwarding}
        handleOpenRenameProjectPopup={props.handleOpenRenameProjectPopup}
        handleOpenDeleteProjectPopup={props.handleOpenDeleteProjectPopup}
        handleOpenSetupProjectPopup={props.handleOpenSetupProjectPopup}
        handleOpenSetupProjectPopupTts={props.handleOpenSetupProjectPopupTts}
        handleOpenExportationPopup={props.handleOpenExportationPopup}
        labelCaptionsOfProject={labelCaptionsOfProject}
        cardWidth={cardWidth}
        cardHeight={cardHeight}
        thumbnailHeight={thumbnailHeight}
        thumbnailPadding={THUMBNAIL_PADDING}
        currentAccountHasCurrentSubscription={currentAccountHasCurrentSubscription}
        handleOpenRenewSubscriptionPopup={props.handleOpenRenewSubscriptionPopup}
      />
    )
  })

  const renderProjects = () => {
    return (
      <React.Fragment>
        {projectsList}
      </React.Fragment>
    )
  }

  const renderScrollingLoader = () => {
    return (
      <div className="ProjectsTableGrid__loader">
        <ScrollingLoader />
      </div>
    )
  }

  const renderFooter = () => {
    return (
      <div className="ProjectsTableGrid__footer-container">
        <hr/>
        <div className="ProjectsTableGrid__footer">
          You've reached the end of the list.
        </div>
      </div>
    )
  }

  return (
    <React.Fragment>
    <div
      className={className}
      ref={gridRef}
    >
      {hasValidCurrentWorkspace && renderNewProject()}
      {renderProjects()}
      {loading && renderScrollingLoader()}
      {showFooter && renderFooter()}
    </div>
    </React.Fragment>
  )
}

export default ProjectsTableGrid
